import { createContext } from "react";
import type { AuthClaims } from "./claims";

/**
 * Vendor-neutral auth shape exposed to apps. Apps depend on this, never on react-oidc-context.
 */
export interface Identity {
  sub: string;
  email?: string;
  name?: string;
}

export interface LoginOptions {
  /**
   * Skip the identity-provider chooser and go straight to one provider.
   *
   * Plain OIDC has no standard parameter for this, so the value is passed through as an extra
   * authorization-request query parameter and the ISSUER decides what it means — Keycloak reads
   * `kc_idp_hint` and jumps to the matching alias ("google"). Deliberately typed as a free string
   * rather than an enum of our providers: the alias belongs to the realm configuration
   * (infra/keycloak-realm/google.tf), not to this package, and hard-coding the set here would put
   * a second source of truth in the frontend.
   */
  idpHint?: string;
  /**
   * The OIDC `prompt`: "login" makes the issuer re-authenticate even with a live session, which is how
   * a sensitive screen gets the fresh sign-in it asks for.
   */
  prompt?: "login";
  /** The OIDC `login_hint`: pre-fills who is signing in. */
  loginHint?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: Identity | null;
  /**
   * Advisory claims decoded from the access token (roles/tenant). Refreshes with the token; the
   * server remains the authority (features/roles-and-permissions.md). Empty when signed out.
   */
  claims: AuthClaims;
  login: (options?: LoginOptions) => void;
  logout: () => void;
  getToken: () => string | undefined;
  /**
   * Obtain a NEW access token, so claims minted server-side since sign-in become visible.
   *
   * Needed because claims are attached by the realm's protocol mappers at token-issue time: after
   * signup provisions an account, the token already in hand still has no `tenant_id`, and every
   * tenant-scoped call keeps failing until a fresh one is fetched. Resolves once the new token is
   * live, so callers can await it before navigating into the app.
   *
   * Rejects if renewal fails (expired session, revoked refresh token) — treat that as "send them
   * back through sign-in", not as a transient error to swallow.
   */
  refreshClaims: () => Promise<void>;
}

/**
 * Internal seam shared by BOTH auth modes: the OIDC path (AuthProvider → OidcStateBridge) and the
 * dev-only stub path (StubAuthProvider) each populate this context, and `useAuth` only ever reads
 * it — so consumers are mode-agnostic and no hook is ever conditional.
 */
export const AuthStateContext = createContext<AuthState | null>(null);
